import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import Fuse from "fuse.js";
import { Seo } from "@/components/Seo";
import { AppShell } from "@/components/AppShell";
import { CalculatorCard } from "@/components/CalculatorCard";
import { CategoryCard, SuperCategoryCard } from "@/components/CategoryCard";
import { SearchBox } from "@/components/SearchBox";
import {
  publicCalculators,
  categories,
  featuredCalculators,
  getCalculatorsByCategory,
  superCategories,
  getSuperCategoryCalcs,
  getSuperCategoryCategories,
  WORKING_CALCULATOR_COUNT,
  WORKING_CALCULATOR_COUNT_LABEL,
} from "@/data/calculators";
import { useRecentlyViewed } from "@/lib/recentlyViewed";
import { withTrailingSlash } from "@/lib/urls";

const homeFuse = new Fuse(publicCalculators, {
  keys: [
    { name: "title", weight: 2.5 },
    { name: "keywords", weight: 1.5 },
    { name: "categoryName", weight: 1 },
    { name: "description", weight: 0.7 },
  ],
  threshold: 0.38,
  includeScore: true,
  minMatchCharLength: 2,
});

export function Home() {
  const [query, setQuery] = useState(() =>
    typeof window !== "undefined" ? (new URLSearchParams(window.location.search).get("q") ?? "") : ""
  );
  const recentItems = useRecentlyViewed();

  useEffect(() => {
    const next = query ? `?q=${encodeURIComponent(query)}` : window.location.pathname;
    window.history.replaceState(null, "", next);
  }, [query]);

  const results = useMemo(() => {
    const term = query.trim();
    if (!term) return featuredCalculators;
    return homeFuse.search(term).map((r) => r.item).slice(0, 24);
  }, [query]);

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "United Calculator",
    url: typeof window !== "undefined" ? window.location.origin : "",
    description: `${WORKING_CALCULATOR_COUNT_LABEL} free online calculators for finance, health, math, science, construction, sports and everyday life. No signup required.`,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${typeof window !== "undefined" ? window.location.origin : ""}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <AppShell>
      <Seo
        title={`${WORKING_CALCULATOR_COUNT_LABEL} Free Online Calculators — Finance, Health, Math & More | UnitedCalculator`}
        description={`${WORKING_CALCULATOR_COUNT_LABEL} free online calculators for finance, health, math, science, construction, sports and more. Every calculator includes the tool, formula, examples and FAQs. No signup required.`}
        canonicalPath="/"
        schema={websiteSchema}
      />
      <main>
        {/* ── Hero ── */}
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">{WORKING_CALCULATOR_COUNT_LABEL} free tools — no signup required</p>
            <h1>{WORKING_CALCULATOR_COUNT_LABEL} free calculators, instantly.</h1>
            <p>
              Finance, health, math, science, unit conversion, business, food, sports, education, and daily
              planning. Every calculator includes the tool, formula notes, examples, FAQs, and related guides.
            </p>
            <div className="hero-actions">
              <a href="#categories" className="button primary">Explore categories</a>
              <a href="#search" className="button ghost">Search calculators</a>
            </div>
          </div>
          <aside className="hero-panel" aria-label="Calculator library summary">
            <div><strong>{WORKING_CALCULATOR_COUNT.toLocaleString("en-GB")}</strong><span>working calculator pages</span></div>
            <div><strong>{categories.length}</strong><span>major categories</span></div>
            <div><strong>12</strong><span>interactive formula engines</span></div>
          </aside>
        </section>

        {/* ── Trust bar ── */}
        <div className="trust-bar" aria-label="Site statistics">
          <div className="trust-stat"><strong>{WORKING_CALCULATOR_COUNT.toLocaleString("en-GB")}</strong><span>working calculators</span></div>
          <div className="trust-stat"><strong>{categories.length}</strong><span>topic categories</span></div>
          <div className="trust-stat"><strong>12</strong><span>formula engines</span></div>
          <div className="trust-stat"><strong>6</strong><span>FAQs per page</span></div>
          <div className="trust-stat"><strong>Free</strong><span>no signup needed</span></div>
        </div>

        {/* ── Five collections ── */}
        <section className="section-block">
          <div className="section-heading">
            <p className="eyebrow">Browse by topic area</p>
            <h2>Five calculator collections</h2>
            <p>Every calculator is organised into one of five topic areas. Click a collection to browse all the tools, sub-categories, and guides it contains.</p>
          </div>
          <div className="super-category-grid">
            {superCategories.map((group) => (
              <SuperCategoryCard
                key={group.slug}
                group={group}
                count={getSuperCategoryCalcs(group.slug).length}
                subCategories={getSuperCategoryCategories(group.slug)}
              />
            ))}
          </div>
        </section>

        {/* ── Search + results (combined, no auto-scroll) ── */}
        <section id="search" className="section-block">
          <div className="section-heading">
            <p className="eyebrow">Find any calculator</p>
            <h2>Search {WORKING_CALCULATOR_COUNT_LABEL} calculators</h2>
            <p>Type any topic — finance, health, math, construction, sports — and results appear instantly below.</p>
          </div>
          <SearchBox value={query} onChange={setQuery} />
          <div className="card-grid" style={{ marginTop: "1.5rem" }}>
            {results.length > 0
              ? results.map((calculator) => <CalculatorCard key={calculator.slug} calculator={calculator} />)
              : (
                <p style={{ gridColumn: "1 / -1", textAlign: "center", color: "var(--muted-foreground)", padding: "2rem 0" }}>
                  No results for &ldquo;{query}&rdquo; — try a different term.
                </p>
              )
            }
          </div>
          {!query && (
            <p style={{ textAlign: "center", marginTop: "1rem", fontSize: "0.875rem", color: "var(--muted-foreground)" }}>
              Showing featured calculators. Start typing to search.
            </p>
          )}
        </section>



        {/* ── Recently viewed (only when no active search) ── */}
        {recentItems.length > 0 && !query && (
          <section className="section-block recent-viewed-section">
            <div className="section-heading">
              <p className="eyebrow">Pick up where you left off</p>
              <h2>Recently viewed</h2>
            </div>
            <div className="recent-grid">
              {recentItems.map((item) => (
                <Link key={item.slug} href={withTrailingSlash(`/calculator/${item.slug}`)} className="recent-card">
                  <span className="recent-cat">{item.categoryName}</span>
                  <span className="recent-title">{item.title}</span>
                  <ArrowRight size={14} className="recent-arrow" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── All 16 categories ── */}
        <section id="categories" className="section-block">
          <div className="section-heading">
            <p className="eyebrow">Browse by category</p>
            <h2>Calculator categories</h2>
            <p>Each category page works like a focused mini-library with topic clusters, descriptions, internal links, and calculator pages targeting specific search intent.</p>
          </div>
          <div className="category-grid">
            {categories.map((category) => (
              <CategoryCard
                key={category.slug}
                category={category}
                count={getCalculatorsByCategory(category.slug).length}
              />
            ))}
          </div>
        </section>

        {/* ── EEEAT ── */}
        <section className="eeeat-section section-block">
          <div className="section-heading">
            <p className="eyebrow">Accuracy and transparency</p>
            <h2>Why United Calculator</h2>
            <p>Every page follows the same content standard: formula derivation, a worked example with real numbers, applicable laws or professional guidance, 6 plain-language FAQs, and links to 8 related tools.</p>
          </div>
          <div className="eeeat-grid">
            <div className="eeeat-card">
              <h3>Verified formulas</h3>
              <p>Each formula engine is based on published standards — TILA for loan calculations, METS tables for energy expenditure, ASTM standards for materials, and equivalents across 16 subject areas. The formula is shown in a code block on every result card so you can verify the calculation independently.</p>
            </div>
            <div className="eeeat-card">
              <h3>Professional context, not professional advice</h3>
              <p>Finance and health calculators cover applicable legislation (CFPB, NHS, OSHA) and reference professional thresholds (e.g. debt-to-income ratios accepted by lenders, WHO BMI categories). Every page includes a disclaimer — these tools are planning aids. Consequential decisions should be reviewed with a qualified professional.</p>
            </div>
            <div className="eeeat-card">
              <h3>No account, no tracking, no fee</h3>
              <p>Calculations run entirely in your browser. No data leaves your device. There are no login walls, no usage limits, no subscription tiers, and no premium paywalls. The only data stored is nothing — no cookies are set beyond what your browser requires to render the page.</p>
            </div>
            <div className="eeeat-card">
              <h3>Transparent methodology</h3>
              <p>Smart defaults come from published benchmarks — median UK household income from ONS, US average mortgage rate from Freddie Mac weekly surveys, average commute distances from the US Census ACS. Defaults are documented in the article body on each calculator page.</p>
            </div>
          </div>
          <div className="eeeat-links">
            <Link href="/about">About</Link>
            <Link href="/disclaimer">Disclaimer</Link>
            <Link href="/privacy-policy">Privacy policy</Link>
            <Link href="/terms-and-conditions">Terms</Link>
            <Link href="/legal">Compliance Hub</Link>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="how-it-works section-block">
          <div className="section-heading">
            <p className="eyebrow">Simple by design</p>
            <h2>How United Calculator works</h2>
            <p>Three steps from question to answer — no account, no subscription, no ads cluttering the result.</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number" aria-hidden="true">1</div>
              <h3>Search or browse</h3>
              <p>Type a topic into the search box or click a category. Each category is a mini-library of calculators targeting specific questions — loan payments, BMI, tile counts, carbon footprint, and more.</p>
            </div>
            <div className="step-card">
              <div className="step-number" aria-hidden="true">2</div>
              <h3>Enter your values</h3>
              <p>Smart defaults are pre-filled so you get an immediate example result. Replace any field with your own data. Click the <strong>?</strong> icon on any field for a plain-language explanation of what that input means.</p>
            </div>
            <div className="step-card">
              <div className="step-number" aria-hidden="true">3</div>
              <h3>Read, copy, and share</h3>
              <p>The result updates in real time. The formula is shown in a code block below the answer. Copy the result to your clipboard, share it to 8 social platforms, or scroll down for the full explanation, FAQs, and 8 related calculators.</p>
            </div>
          </div>
        </section>
      </main>
    </AppShell>
  );
}
